import { DEFAULT_QUERY_LIMIT, DEFAULT_QUERY_MAX } from "@client-shared/lib/constants";
import { fetchAllPurchaseOrders } from "@client-shared/providers/purchase-orders";
import type { CarrierCustomerAccountBinding, PurchaseOrder } from "@shared/types";
import { useQuery } from "@tanstack/react-query";

interface WaitingOrderCountsResult {
  readonly waitingCounts: Record<string, number>;
  readonly totalCount: number;
  readonly isLoading: boolean;
}

/**
 * Groups orders by carrier key (customerAccountNumber).
 * Handles comma-separated customerAccountNumber values on a carrier.
 * Orders with an unrecognised account are excluded.
 */
export function groupOrdersByCarrier(
  orders: readonly PurchaseOrder[],
  carrierDetails: readonly CarrierCustomerAccountBinding[]
): Record<string, number> {
  const accountToCarrierKey = new Map<string, string>();
  for (const carrier of carrierDetails) {
    for (const account of carrier.customerAccountNumber.split(",").map((a) => a.trim())) {
      accountToCarrierKey.set(account, carrier.customerAccountNumber);
    }
  }

  const counts: Record<string, number> = {};
  for (const order of orders) {
    const carrierKey = accountToCarrierKey.get(order.customerAccountNumber);
    if (carrierKey) {
      counts[carrierKey] = (counts[carrierKey] ?? 0) + 1;
    }
  }
  return counts;
}

/**
 * Fetches all WaitingToBeFulfilled orders for the vendor (paginating if needed)
 * and groups them by carrier (customerAccountNumber) client-side.
 *
 * Only enabled when vendorKey is defined and there is at least one carrier.
 */
export function useWaitingOrderCounts(
  vendorKey: string | undefined,
  carrierDetails: readonly CarrierCustomerAccountBinding[] | undefined
): WaitingOrderCountsResult {
  const { data, isLoading } = useQuery({
    queryKey: ["waitingOrderCounts", vendorKey],
    queryFn: async () => {
      if (!vendorKey) return { orders: [], complete: true };
      return fetchAllPurchaseOrders({
        vendorKey,
        orderStatus: ["WaitingToBeFulfilled"],
        limit: DEFAULT_QUERY_LIMIT,
        max: DEFAULT_QUERY_MAX,
        sortByCreatedAt: "desc",
      });
    },
    enabled: !!vendorKey && !!carrierDetails?.length,
    refetchOnWindowFocus: true,
  });

  const orders = data?.orders;

  if (!orders || !carrierDetails) {
    return { waitingCounts: {}, totalCount: 0, isLoading };
  }

  const waitingCounts = groupOrdersByCarrier(orders, carrierDetails);
  const totalCount = Object.values(waitingCounts).reduce((sum, n) => sum + n, 0);

  return { waitingCounts, totalCount, isLoading };
}
